import { Nullable } from '@aibees/shared/definition';
import { useMount, useQueryStringState } from '@aibees/shared/hooks';
import { fDate } from '@aibees/shared/utils';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { LocalizationProvider, StaticDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { memo, useState } from 'react';
import { Icon } from '../icon';
import * as SX from './sx';
import type { DateRangePickerByQueryParamsProps as Props } from './types';
import { formatButtonDateValues } from './utils';

export const DateRangePickerByQueryParams = memo(
  ({ defaultStartDate, defaultEndDate }: Props) => {
    const { params, setParams } = useQueryStringState();
    const [open, setOpen] = useState(false);

    const [startDate, setStartDate] = useState<Nullable<Date>>(
      params.startDate ? new Date(params.startDate) : new Date(defaultStartDate)
    );
    const [endDate, setEndDate] = useState<Nullable<Date>>(
      params.endDate ? new Date(params.endDate) : new Date(defaultEndDate)
    );

    const buttonDateValue =
      formatButtonDateValues(startDate) +
      ' - ' +
      formatButtonDateValues(endDate);

    useMount(() => {
      setParams({ startDate: defaultStartDate, endDate: defaultEndDate });
    });

    const handleClickOpen = () => {
      setOpen(true);
    };

    const handleClose = () => {
      setOpen(false);
    };

    const handleSave = () => {
      new Promise((resolve) => {
        setOpen(false);
        setTimeout(() => {
          resolve(true);
        }, 100);
      }).then(() => {
        if (!!startDate && !!endDate) {
          setParams({
            startDate: fDate(startDate, 'yyyy-MM-dd'),
            endDate: fDate(endDate, 'yyyy-MM-dd'),
          });
        }
      });
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleClickOpen}
          startIcon={
            <Icon name="today-calendar" size={18} color="text.primary" />
          }
          color="default"
        >
          {buttonDateValue}
        </Button>
        <Dialog
          open={open}
          onClose={handleClose}
          transitionDuration={{ exit: 0 }} // this has to be 0 otherwise by setting params and closinng the dialog sometimes the dialog mask doesn't disappear
          maxWidth="md"
          fullWidth={true}
          sx={{
            '& .MuiDialog-paper': {
              width: 'auto',
            },
          }}
        >
          <DialogTitle>Select Date Range</DialogTitle>
          <DialogContent>
            <Box sx={SX.boxWrapper}>
              <Box sx={SX.datePickerWrapper}>
                <StaticDatePicker
                  displayStaticWrapperAs="desktop"
                  openTo="day"
                  value={dayjs(startDate)}
                  onChange={(newValue) =>
                    setStartDate(newValue?.toDate() ?? null)
                  }
                />
              </Box>
              <Box sx={SX.datePickerWrapper}>
                <StaticDatePicker
                  displayStaticWrapperAs="desktop"
                  openTo="day"
                  value={dayjs(endDate)}
                  onChange={(newValue) =>
                    setEndDate(newValue?.toDate() ?? null)
                  }
                  minDate={dayjs(startDate)}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    );
  }
);

DateRangePickerByQueryParams.displayName = 'DateRangePickerByQueryParams';
